import { LitElement, html, query, property } from 'lit-element'
import { register } from '@polleverywhere/hmr-custom-element'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { defineElementSlot } from '@polleverywhere/element-helpers'
import { contextualize, cutlassEvent } from '@polleverywhere/cutlass'
import { validate as validateViewport } from './viewports'
import '@polleverywhere/pe-graphic'
import '@polleverywhere/pe-tooltip'
import './divider'
import './item'
import './panel'
import './spacer'
import './navigation.scss'

/**
 * Renders a navigation element to manage L1 and an optional L2 panel. Declare
 * navigation items in the list. Create sections of navigation items by
 * inserting spacers and/or dividers between the items.
 *
 * ```
 * import '@polleverywhere/pe-navigation'
 * ```
 *
 * ## L1 sections
 * Create different sections of navigation items using `<pe-navigation-spacer>`
 * and `<pe-navigation-divider>` elements placed between the items. The spacer
 * creates empty space between its adjacent items. The divider draws a line
 * separator between adjacent items.
 *
 * ```
 * <pe-navigation>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 *
 *   <!-- Second set of nav items -->
 *   <pe-navigation-spacer></pe-navigation-spacer>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 *
 *   <!-- Third set of nav items -->
 *   <pe-navigation-divider></pe-navigation-divider>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 * </pe-navigation>
 * ```
 *
 * ## L2 panel
 * L2 is declared using `slot=l2` and must be a `<pe-navigation-panel>`
 * composed of navigation items and dividers to create sections. L2 is optional
 * and is a place to create another layer of navigational items that belong to
 * the active L1 item.
 *
 * ```
 * <pe-navigation>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 *   <pe-navigation-item ...><pe-navigation-item>
 *
 *   <div slot='l2'>
 *     <pe-navigation-panel ...>
 *       <pe-navigation-item ...><pe-navigation-item>
 *       <pe-navigation-item ...><pe-navigation-item>
 *       <pe-navigation-item ...><pe-navigation-item>
 *
 *       <pe-navigation-divider></pe-navigation-divider>
 *       <pe-navigation-item ...><pe-navigation-item>
 *       <pe-navigation-item ...><pe-navigation-item>
 *
 *       <pe-navigation-divider></pe-navigation-divider>
 *       <pe-navigation-item ...><pe-navigation-item>
 *     </pe-navigation-panel>
 *   </div>
 * </pe-navigation>
 * ```
 *
 * @element pe-navigation
 *
 * @slot - Default slot containing all navigation items, spacers and dividers.
 * @slot l2 - Insert a navigation panel for the L2 nav.
 *
 * @prop {string} active - Set or get the currently active L1 nav item based on
 *  the `name` attribute assigned.
 * @prop {HTMLElement} activeItem - (READONLY) Returns the currently active item.
 * @prop {HTMLElement} panel - (READONLY) Returns the navigation panel if one
 *  exists.
 * @prop {boolean} showLabels - Show or hide L1 navigation item labels.
 * @prop {boolean} hideTooltips - Show or hide L1 navigation item tooltips.
 * @prop {string} viewport - Get or set the viewport size to expand or condense
 *  the number of visible nav items and dividers. When no viewport size is
 *  specified, the largest size is implied, rendering all nav items and dividers
 *  visible. Valid values are: `sm`,`md`,`lg`.
 * @prop {string} placeholder - Render a placeholder of the nav in CSS while the JS loads
 *  Use 'l1', 'l2', or 'l2-toggle'. Note this auto-clears once loaded.
 *
 * @cssprop --pe-navigation-color - Foreground color
 * @cssprop --pe-navigation-bg-color - Background color for L1
 * @cssprop --pe-navigation-active-bg-color - Active background color for L1
 * @cssprop --pe-navigation-panel-bg-color - Background color for L2
 * @cssprop --pe-navigation-panel-active-bg-color - Active background color for
 *  L2
 * @cssprop --pe-navigation-panel-closed-bg-color - Background color for L2 in
 *  closed state
 * @cssprop --pe-navigation-panel-max-width - Max width for the L2 panel to stretch
 *  out to when it contains longer nav items.
 * @cssprop --pe-navigation-panel-min-width - Min width for the L2 panel to shrink to
 *  when nav items are short.
 * @cssprop --pe-navigation-panel-max-height - Max height for the L2 panel to
 *  shrink to when viewport size is set and nav is oriented vertically.
 * @cssprop --pe-navigation-panel-min-height - Min height for the L2 panel to
 *  shrink to when viewport size is set and nav is oriented vertically.
 *
 * @fires {Object} activating - Fires when navigation item is in progress of
 *  being activated. This event can be cancelled, thus preventing the item from
 *  being activated. Activating nav item is stored under `event.detail.item`.
 * @fires {Object} activated - Fires when activating a new navigation item.
 */
export default class Navigation extends LitElement {
  static get tagName () { return 'pe-navigation' }

  @property({ type: String })
  get active () {
    return this._active
  }

  set active (value) {
    const oldValue = this._active
    if (oldValue === value) return

    // Inform consumers that a new item has been activated.
    const activatingItem = this.querySelector(`pe-navigation-item[name='${value}']`)
    const cancelled = !this.dispatchEvent(new CustomEvent('activating', {
      bubbles: true,
      cancelable: true,
      detail: {
        item: activatingItem
      }
    }))
    if (cancelled) return

    this._active = value
    this.requestUpdate('active', oldValue)
    updateActiveItem.call(this)

    // Inform consumers that the active item changed.
    this.dispatchEvent(new CustomEvent('activated', { bubbles: true }))

    // Track event only when activated state changes.
    if (value) {
      cutlassEvent(this, ['activated', value].join(':'))
    }
  }

  get activeItem () {
    return Array.from(this.querySelectorAll(`.${bem('l1')} pe-navigation-item`))
      .find((item) => item.active)
  }

  @query('pe-navigation-panel')
  panel

  @property({ type: Boolean, reflect: true, attribute: 'show-labels' })
  showLabels = false

  @property({ type: Boolean, reflect: true, attribute: 'hide-tooltips' })
  hideTooltips = false

  @property({ type: String, reflect: true })
  viewport

  @property({ type: String, reflect: true })
  placeholder

  constructor () {
    super()
    contextualize(this, this.tagName)
  }

  connectedCallback () {
    super.connectedCallback()
    defineElementSlot(this)
    defineElementSlot(this, 'l2')
  }

  createRenderRoot () { return this }

  firstUpdated () {
    updateActiveItem.call(this)
    this.placeholder = null

    if (!this.hideTooltips) {
      toggleL1Tooltips.call(this, true)
    }
  }

  updated (changedMap) {
    changedMap.has('viewport') && validateViewport(this.viewport)
    changedMap.has('hideTooltips') && toggleL1Tooltips.call(this, !this.hideTooltips)
  }

  render () {
    const homeLabel = 'Home'

    return html`
      <nav class=${bem()}>
        <div @navigate=${handleNavigate} class=${bem('l1')}>
          <button
            @click=${handleLogoClick}
            aria-label=${homeLabel}
            class=${bem('logo')}
          >
            <pe-tooltip placement='right' group='pe-navigation-item'>
              ${homeLabel}
            </pe-tooltip>
            <pe-graphic name='logomark-blue'></pe-graphic>
          </button>
          ${this.slots()}
        </div>
        <div class=${bem('l2')}>
          ${this.slots('l2')}
        </div>
      </nav>
    `
  }
}

function handleNavigate (e) {
  // Only update the active nav item if the item is not a redirect.
  if (!e.target.href) {
    this.active = e.target.name
  }
}

function handleLogoClick () {
  const firstItem = this.querySelector(`.${bem('l1')} pe-navigation-item a`)
  if (!firstItem) return

  this.querySelector(`.${bem('l1')} pe-navigation-item a`).click()
  cutlassEvent(this, 'logo:click')
}

function updateActiveItem () {
  this.querySelectorAll(`.${bem('l1')} pe-navigation-item`).forEach(item => {
    item.active = item.name && item.name === this.active
  })
}

function toggleL1Tooltips (show = true) {
  const l1Items = this.querySelectorAll(`.${bem('l1')} pe-navigation-item`)
  l1Items.forEach(item => { item.showTooltip = show })
}

const bem = bemBlock(Navigation.tagName)
register(module, Navigation.tagName, Navigation)
